import { useMutation, useQuery } from "@tanstack/react-query";
import { Stack } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import {
  informationTypeByIdQueryOptions,
  informationTypeFormQueryOptions,
  updateInformationTypeAnswersMutationOptions,
  updateInformationTypeClassificationMutationOptions,
} from "../../api/informationTypes";
import { classificationTemplateQueryOptions } from "../../api/classificationTemplate";
import { ClassificationCard } from "../../classification/ClassificationCard";
import { DynamicForm } from "../../dynamicForm";
import { DynamicGrid } from "../../dynamicGrid";
import { Drawer } from "../../shared/Drawer";
import { InformationTypeDrawerHeader } from "./InformationTypeDrawerHeader";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { InformationTypeConnections } from "./InformationTypeConnections";
import { useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import { usePermission } from "../../shared/permissions";

export const InformationTypeMainDrawer = () => {
  const informationTypeId = DynamicGrid.useSelectedRowId();
  const currentOrganisation = useCurrentOrganisation();
  const { hasPermission } = usePermission();
  const informationTypeByIdQuery = useQuery(informationTypeByIdQueryOptions(informationTypeId));
  const formQuery = useQuery(informationTypeFormQueryOptions());
  const templateQuery = useQuery({
    ...classificationTemplateQueryOptions(),
    enabled: currentOrganisation.classificationEnabled,
  });
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const updateAnswersMutation = useMutation(updateInformationTypeAnswersMutationOptions());
  const updateClassificationMutation = useMutation(
    updateInformationTypeClassificationMutationOptions(informationTypeId)
  );

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!informationTypeByIdQuery.data || !formQuery.data) {
          return <Drawer.Loading />;
        }
        if (currentOrganisation.classificationEnabled && !templateQuery.data) {
          return <Drawer.Loading />;
        }
        const informationType = informationTypeByIdQuery.data;
        const canEdit = hasPermission("nova-forms-api:information-type:update") && !informationType.archivedAt;
        return (
          <DynamicForm.Provider
            onSave={answer =>
              updateAnswersMutation.mutateAsync({ informationTypeId: informationType.id, answers: [answer] })
            }
            isReadOnly={!canEdit}
            isSaving={updateAnswersMutation.isPending}
            isSaveError={updateAnswersMutation.isError}
            answers={informationType.answers}
          >
            {({ formState }) => (
              <>
                <InformationTypeDrawerHeader
                  informationType={informationType}
                  onClose={() => navigate(paths.informationTypesGrid())}
                  status={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                  activeTab="overview"
                />
                <Drawer.Content>
                  <DynamicForm.InlineForm id={informationType.id} form={formQuery.data} />
                  {informationType.classification && templateQuery.data && (
                    <Stack sx={{ mt: 6 }}>
                      <DynamicForm.InlineFormSection
                        title={t("InformationType.Classification.SectionTitle")}
                        subtitle={t("InformationType.Classification.SectionSubtitle")}
                      >
                        <ClassificationCard
                          key={informationType.id}
                          template={templateQuery.data.template}
                          customerCustomizations={templateQuery.data.customerCustomizations}
                          criticalityTable={templateQuery.data.criticalityTable ?? []}
                          scores={informationType.classification.scores}
                          criticality={informationType.classification.criticality}
                          rationale={informationType.classification.rationale}
                          isReadOnly={!canEdit}
                          onSave={updateClassificationMutation.mutateAsync}
                        />
                      </DynamicForm.InlineFormSection>
                    </Stack>
                  )}
                  <Stack sx={{ gap: 3, mt: 6 }}>
                    <DynamicForm.InlineFormSection
                      title={t("InformationType.Connection.StoredIn.Title")}
                      subtitle={t("InformationType.Connection.StoredIn.Subtitle")}
                    >
                      <InformationTypeConnections
                        informationTypeId={informationType.id}
                        connectionType="stored"
                        connections={informationType.connectedSystemsInfo.storedConnections}
                        canEdit={canEdit}
                      />
                    </DynamicForm.InlineFormSection>
                    <DynamicForm.InlineFormSection
                      title={t("InformationType.Connection.UsedIn.Title")}
                      subtitle={t("InformationType.Connection.UsedIn.Subtitle")}
                    >
                      <InformationTypeConnections
                        informationTypeId={informationType.id}
                        connectionType="used"
                        connections={informationType.connectedSystemsInfo.usedConnections}
                        canEdit={canEdit}
                      />
                    </DynamicForm.InlineFormSection>
                  </Stack>
                </Drawer.Content>
              </>
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};
